import React, { useRef, useState, useMemo } from 'react';
import type { FormEvent } from 'react';
import { v4 } from 'uuid';
import { useBusinessLogging } from '@alfa-bank/site-form-utils/decorators/business-logging';
import { getPlatformID } from '@alfa-bank/site-helpers-common/browser/get-platform-id';
import { SME_FORM_HOST } from '@alfa-bank/site-sme-env-config';
import type { FormApi } from 'final-form';
import type { FormRenderProps } from 'react-final-form';

import { removeCookies, getValidatorFromForm } from '../../helpers';

import { NativeForm } from './components/native-form';
import { FormContext } from './context';
import type { IForm, TErrorFields, TFields, TFormValues } from './models';

/**
 * Компонент формы. Использует react-final-form
 * Также пробрасывает информацию о форме и гео дочерним компонентам через React Context.
 */
export const Form = ({
    FinalForm,
    action,
    businessLoggingSource: source = 'RKO_AO',
    children,
    className,
    cookie,
    id,
    initialValues,
    isBusinessLoggingEnabled = false,
    method = 'POST',
    onSubmit,
    preventSubmit,
    product = 'sme',
    validateOnBlur = false,
}: IForm) => {
    const businessUid = useMemo(() => v4(), []);
    const [errorFields, setErrorFields] = useState<TErrorFields<TFields>>({});
    // TODO: useState нужно заменить на useContext этой формы
    const formRef = useRef<HTMLFormElement>(null);
    const platformId = useRef(getPlatformID() || 'alfasite').current;

    // SME_FORM_HOST используется для тестирования интеграции в альфа офис (тестировщикам нужно), на проде не используется.
    const formAction = SME_FORM_HOST.value() || action;

    const decorator = useBusinessLogging({
        actions: {
            platformId,
        },
        product,
        source,
        recipient: action,
    });

    const updateErrorFields = (newFields: TErrorFields<TFields>) =>
        setErrorFields(oldFields => ({ ...oldFields, ...newFields }));

    const deleteErrorField = (field: keyof TErrorFields<TFields>) => {
        delete errorFields[field];
        setErrorFields({ ...errorFields });
    };

    // TODO: добавить во все обертки филдов поддержку disabled, на примере InputField
    const [disabled, setDisabled] = useState<boolean>();

    const disableFields = () => setDisabled(true);
    const enableFields = () => setDisabled(undefined);

    /** Нативный обработчик submit формы */
    const nativeSubmit =
        (formApi: FormApi<TFormValues>, handleSubmit: FormRenderProps['handleSubmit']) =>
        (e: FormEvent) => {
            if (preventSubmit) e.preventDefault();

            /** Вешаем декоратор для отправки данных в билог */
            if (isBusinessLoggingEnabled) {
                decorator(formApi);
            }
            /** Проверяем что все поля валидные */
            if (getValidatorFromForm(formApi as unknown as FormApi<TFields>)?.()) {
                /** Отправляем данные в билог при нативной отправке формы
                 *  вызывая системный submit
                 */
                formApi.submit();
                /** Удаляем куку no_sms_draft при успешном подтверждении телефона */
                removeCookies(cookie);

                /** Нативыный обработчик submit редиректит на action */
                return handleSubmit();
            }
            /** Если поля не валидные вызываем submit для валидации */
            formApi.submit();
            e.preventDefault();
        };

    return (
        <div className={className}>
            <FinalForm<TFormValues>
                initialValues={{
                    ...initialValues,
                    businessUid,
                    cookie,
                }}
                onSubmit={onSubmit ? onSubmit : () => {}}
                render={({ form: formApi, handleSubmit }) => {
                    return (
                        <NativeForm
                            action={formAction}
                            decorator={decorator}
                            id={id}
                            innerRef={formRef}
                            isBusinessLoggingEnabled={isBusinessLoggingEnabled}
                            method={method}
                            onSubmit={nativeSubmit(formApi, handleSubmit)}
                        >
                            <FormContext.Provider
                                value={{
                                    disabled,
                                    deleteErrorField,
                                    disableFields,
                                    enableFields,
                                    errorFields,
                                    updateErrorFields,
                                }}
                            >
                                {children}
                            </FormContext.Provider>
                        </NativeForm>
                    );
                }}
                validateOnBlur={validateOnBlur}
            />
        </div>
    );
};
